import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/react';
import { isEmpty, noop } from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';

import {
  ActionModalControlPanel,
  type ActionModalControlPanelProps,
  type Subsidiary,
} from '@revelio/core';
import type { Item } from '@revelio/iso-utility';

import {
  CompanySearchTree,
  type CompanySearchTreeHandle,
} from '../../../../shared/components/company-search-tree/company-search-tree';
import type { TreeData } from '../../../../shared/components/tree/useTreeApi';
import { useSelectionLists } from '../../../../shared/selection-lists/hooks/useSelectionLists';
import type { CompanyResultItem } from '../../../../shared/types/company-result-item';

type PlaceholderKey = 'company' | 'rics_k10' | 'rics_k50';
const COMPANY_PLACEHOLDER_KEYS: PlaceholderKey[] = [
  'company',
  'rics_k10',
  'rics_k50',
];
const EMPTY_KEYS: PlaceholderKey[] = [];

const sortByEmployeeCount = (a: Item, b: Item) =>
  Number(b.raw_emp_count ?? 0) - Number(a.raw_emp_count ?? 0);

export interface CompanySelectionsModalProps
  extends Partial<ActionModalControlPanelProps> {
  header?: string;
  isOpen: boolean;
  onOpen?: () => void;
  onClose?: () => void;
  onCancel?: () => void;
  submitHandler?: (selections: Subsidiary[]) => void;
  entityMenuHandlers?: {
    text: string;
    handler: (selections: Subsidiary[]) => void;
  }[];
  submitIsLoading?: boolean;
  setSubmitIsLoading?: React.Dispatch<React.SetStateAction<boolean>>;
  treeHeight?: number;
  closeOnSubmit?: boolean;
  initialSelections?: Record<string, Subsidiary>;
  showPlaceholders?: boolean;
  allowEmptySubmit?: boolean;
}

export function CompanySelectionsModal({
  header = '',
  isOpen,
  onClose = noop,
  onCancel,
  submitHandler,
  entityMenuHandlers,
  submitIsLoading,
  submitText,
  treeHeight = 310,
  closeOnSubmit = true,
  initialSelections = {},
  showPlaceholders = false,
  allowEmptySubmit = false,
}: CompanySelectionsModalProps) {
  const [selections, setSelections] = useState<
    Record<string, CompanyResultItem>
  >(initialSelections as unknown as Record<string, CompanyResultItem>);

  const treeRef = useRef<CompanySearchTreeHandle>(null);

  const handleReset = () => {
    treeRef.current?.handleClearSelections();
  };

  const handleSubmit = () => {
    const value = Object.values(selections) as unknown as Subsidiary[];

    submitHandler?.(value);
    setSelections({});

    if (closeOnSubmit) {
      onClose();
    }
  };

  const adaptedMenuHandlers = entityMenuHandlers?.map((menuItem) => ({
    ...menuItem,
    handler: () =>
      menuItem.handler(Object.values(selections) as unknown as Subsidiary[]),
  }));

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional dependency list
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setSelections(
      initialSelections as unknown as Record<string, CompanyResultItem>
    );
  }, [isOpen]);

  const selectionListData = useSelectionLists(
    showPlaceholders ? COMPANY_PLACEHOLDER_KEYS : EMPTY_KEYS
  );

  const placeholderListNodes = useMemo<TreeData[]>(() => {
    if (!selectionListData) return [];

    const { company, rics_k10, rics_k50 } = selectionListData;
    if (!company || !rics_k10) return [];

    // Build rics_k50 -> rics_k10 lookup
    const k50ToK10: Record<string, string> = {};
    if (rics_k50) {
      for (const item of rics_k50) {
        if (item?.id != null && item.rics_k10 != null) {
          k50ToK10[String(item.id)] = String(item.rics_k10);
        }
      }
    }

    // Group companies by rics_k10
    const companiesByK10: Record<string, TreeData[]> = {};
    for (const c of company) {
      if (!c) continue;
      const k10Id = k50ToK10[String(c.rics_k50 ?? '')];
      if (!k10Id) continue;

      (companiesByK10[k10Id] ??= []).push({
        id: `company.${c.id}`,
        name: String(c.longName ?? c.shortName ?? c.id),
        depth: 1,
        nodeCount: Number(c.headcount ?? 0),
      });
    }

    // Build tree: rics_k10 as parents, companies as children
    const nodes: TreeData[] = rics_k10
      .filter((k10): k10 is NonNullable<typeof k10> => k10 != null)
      .map((k10) => ({
        id: `rics_k10.${k10.id}`,
        name: String(k10.longName ?? k10.shortName ?? k10.id),
        depth: 0,
        children: companiesByK10[String(k10.id)] ?? [],
      }));

    // Sort "Other" to end
    nodes.sort((a, b) => {
      const aIsOther = a.name === 'Other';
      const bIsOther = b.name === 'Other';
      if (aIsOther !== bIsOther) return aIsOther ? 1 : -1;
      return 0;
    });

    return nodes;
  }, [selectionListData]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent minHeight="530px">
        <ModalHeader
          borderBottom="1px solid #E5EBF1"
          fontSize="17px"
          fontWeight="600"
          py="12px"
        >
          {header}
        </ModalHeader>
        <ModalCloseButton size="sm" />
        <ModalBody py="16px">
          <CompanySearchTree
            ref={treeRef}
            placeholderListNodes={placeholderListNodes}
            treeHeight={treeHeight}
            selections={selections}
            setSelections={setSelections}
            sortFn={sortByEmployeeCount}
            autoFocus
          />
        </ModalBody>
        <ModalFooter borderTop="1px solid #E5EBF1">
          <ActionModalControlPanel
            submitText={submitText}
            onClose={onClose}
            onCancel={onCancel}
            onSubmit={handleSubmit}
            menuHandlers={adaptedMenuHandlers}
            onReset={handleReset}
            submitIsDisabled={!allowEmptySubmit && isEmpty(selections)}
            submitIsLoading={submitIsLoading}
          />
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
